import { Achievement } from '@/models';
import { getFullMonthName } from '@/helpers';
import { Badge, SummerAchievement, SummerCampaign } from '@/models/leaderboard';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

@Component({
  name: 'CmgLeaderboardBadges',
})
export default class CmgLeaderboardBadges extends Vue {
  selectedCampaignIndex = -1;
  isSideQuestsTabActive = false;

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }  

  get whiteBoxImg(): string | undefined {
    return !this.isMobile
      ? this.$appStore.state.cms.images.loyalty.leaderboardWhiteBoxBadgesError.desktop
      : this.$appStore.state.cms.images.loyalty.leaderboardWhiteBoxBadgesError.mobile;
  }  

  get isInErrorState(): boolean {
    return this.$appStore.state.leaderboard.isInErrorState;
  }
  
  get campaigns(): SummerCampaign[] {
    return this.$appStore.state.leaderboard.summerCampaigns;
  }

  get achievements(): SummerAchievement {
    return this.$appStore.state.leaderboard.achievements;
  }

  get hasCampaigns(): boolean {
    return this.campaigns?.length > 0;
  }

  get earliestCampaignMonth(): number | undefined {
    if (!this.hasCampaigns) {
      return undefined;
    }

    return this.campaigns.reduce((earliest, campaign) => {
      return campaign.month < earliest ? campaign.month : earliest;
    }, this.campaigns[0].month);
  }

  get currentMonthIndex(): number {
    const currentMonth = new Date().getMonth();
    return this.earliestCampaignMonth !== undefined ? 
    Math.max(this.earliestCampaignMonth, currentMonth) : currentMonth;
  }

  get defaultCampaignIndex(): number {
    if (!this.hasCampaigns) {
      return -1;
    }

    const indexForCurrentMonth = this.campaigns.findIndex(c => c.month === this.currentMonthIndex);
    if (indexForCurrentMonth >= 0) {
      return indexForCurrentMonth;
    }

    return this.campaigns.length - 1;
  }

  get activeCampaignIndex(): number {
    if (!this.hasCampaigns) {
      return -1;
    }

    const effectiveIndex = this.selectedCampaignIndex >= 0 ? this.selectedCampaignIndex : this.defaultCampaignIndex;
    return Math.min(effectiveIndex, this.campaigns.length - 1);
  }

  get selectedCampaign(): SummerCampaign | null {
    if (!this.hasCampaigns) {
      return null;
    }

    return this.campaigns[this.activeCampaignIndex];
  }

  get sideQuests(): Badge[] {
    return this.achievements.badges.filter(x => x.badgeType.toLowerCase() === 'summersidequest');
  }

  get hasSideQuests(): boolean {
    return this.sideQuests?.length > 0;
  }

  get sideQuestBadgeUrls(): string[] {
    if (this.hasSideQuests) {
      return this.sideQuests.map(x => x.imageUrl);
    }

    return [];
  }

  getCampaignTabLabel(campaign: SummerCampaign): string {
    return getFullMonthName(new Date(2000, campaign.month, 1));
  }

  onSelectCampaign(index: number) {
    this.selectedCampaignIndex = index;
    this.isSideQuestsTabActive = false;
  }

  onSelectSideQuests() {
    this.isSideQuestsTabActive = true;
  }

  onClickBadge(badge: Badge) {
    if (badge !== undefined) {
      const achievement: Achievement = {
        title: badge.title,
        description: badge.description,
        achievementDate: badge.achievementDate,
        mediaUri: badge.imageUrl,
        achievementId: '',
        tagLine: '',
        medallionText: '',
        pointsEarned: 0,
        shareText: '',
        badgeType: 'extra'
      };
      this.$services.ui.setModalVisibility('achievedBadge', true, { achievement: achievement });
    }
  }  
}
